import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';
import { Building2, Gem, TrendingUp, Rocket, ChevronRight, ChevronLeft, ArrowRight, MapPin, Phone, Mail } from 'lucide-react';

/* ── Scroll-reveal hook ── */
const useInView = (threshold = 0.12, once = true) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); if (once) obs.disconnect(); }
      else if (!once) setInView(false);
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, once]);
  return [ref, inView];
};

/* ── Sector data ── */
const sectors = [
  {
    id: 'real-estate',
    number: '01',
    icon: Building2,
    title: 'Real Estate',
    tagline: 'Building Wealth Through Property',
    accent: '#a855f7',
    accentDark: '#7c3aed',
    accentLight: '#f3e8ff',
    color: 'from-[#360153] to-[#5a0280]',
    stat: { value: '5', label: 'Strategies' },
    mainImage: '/Main_header_building.png',
    summary: 'We pursue comprehensive real estate strategies from urban redevelopment to premium commercial spaces crafting investments that generate lasting value across every market cycle.',
    subsectors: [
      { name: 'SRA Investments',        description: 'Slum Rehabilitation Authority projects that drive urban improvement while delivering strong risk-adjusted returns.',        image: '/SRA.png' },
      { name: 'Ready Flats',            description: 'Fully-developed residential units offering immediate occupancy, stable rental income, and capital appreciation.',           image: '/Ready_flats.png' },
      { name: 'Commercial Units',       description: 'Premium office spaces and retail properties anchored by quality tenants in high-demand locations.',                        image: '/commercial_units.png' },
      { name: 'Land Banking',           description: 'Strategic acquisition of undeveloped land in prime growth corridors, positioned for long-term appreciation.',              image: '/land_banking.png' },
      { name: 'Redevelopment Projects', description: 'Transforming existing structures into high-value residential and commercial assets through modern redevelopment.',         image: '/redevelopment_projects.png' },
    ],
  },
  {
    id: 'bullion',
    number: '02',
    icon: Gem,
    title: 'Bullion - Gold & Silver',
    tagline: 'Preserving Wealth Across Generations',
    accent: '#f59e0b',
    accentDark: '#d97706',
    accentLight: '#fef3c7',
    color: 'from-yellow-600 to-yellow-800',
    stat: { value: '2', label: 'Precious Metals' },
    mainImage: 'https://images.unsplash.com/photo-1610375461246-83df859d849d?w=900&h=700&fit=crop&q=80',
    summary: 'Precious metals form our bedrock hedge against market volatility and inflation, ensuring wealth preservation through every economic cycle with timeless, enduring stability.',
    subsectors: [
      { name: 'Gold',   description: 'A timeless store of value and a reliable safe-haven asset that anchors our portfolio against macroeconomic uncertainty.', image: 'https://images.unsplash.com/photo-1610375461246-83df859d849d?w=900&h=700&fit=crop&q=80' },
      { name: 'Silver', description: 'Industrial demand combined with investment appeal makes silver a dynamic asset for both growth and portfolio stability.',   image: '/Silver.png' },
    ],
  },
  {
    id: 'equities',
    number: '03',
    icon: TrendingUp,
    title: 'Equities',
    tagline: 'Unlocking Value in Public & Private Markets',
    accent: '#10b981',
    accentDark: '#059669',
    accentLight: '#d1fae5',
    color: 'from-emerald-600 to-emerald-800',
    stat: { value: '2', label: 'Market Segments' },
    mainImage: '/Market_Int.png',
    summary: 'We invest across the full equity spectrum from publicly listed companies with strong fundamentals to high-potential private enterprises poised for exponential growth.',
    subsectors: [
      { name: 'Listed Companies',   description: 'Publicly traded firms selected through rigorous fundamental analysis, offering liquidity and transparent price discovery.', image: '/Equity_Investments.png' },
      { name: 'Unlisted Companies', description: 'Early-stage and growth-phase private enterprises where our capital and expertise can create outsized returns.',              image: '/Unlisted_companies.jpg' },
    ],
  },
  {
    id: 'startup-vc',
    number: '04',
    icon: Rocket,
    title: 'Startup & VC Investing',
    tagline: 'Backing the Innovators of Tomorrow',
    accent: '#3b82f6',
    accentDark: '#2563eb',
    accentLight: '#dbeafe',
    color: 'from-blue-700 to-blue-900',
    stat: { value: '2+', label: 'Focus Areas' },
    mainImage: '/Early_stage_startups.jpeg',
    summary: 'We back visionary founders and emerging technologies partnering with early-stage companies that have the potential to redefine entire industries and create generational wealth.',
    subsectors: [
      { name: 'Early-Stage Startups',  description: 'Seed and Series A investments in founders building transformative businesses with scalable, defensible models.', image: '/Startup.png' },
      { name: 'Emerging Technologies', description: 'Deep-tech, fintech, and platform businesses at the frontier of innovation and market disruption.',               image: '/Emerging_Technology.jpg' },
    ],
  },
];

/* ── Subsector card ── */
const SubCard = ({ sub, i, isHov, accent, accentDark, onEnter, onLeave }) => {
  const [ref, inView] = useInView(0.1, true);
  return (
    <div
      ref={ref}
      className="group relative cursor-default overflow-hidden rounded-2xl transition-all duration-300"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'none' : 'translateY(28px)',
        transition: `opacity 0.55s cubic-bezier(0.16,1,0.3,1) ${i * 90}ms, transform 0.55s cubic-bezier(0.16,1,0.3,1) ${i * 90}ms, box-shadow 0.25s ease, border-color 0.25s ease`,
        background: isHov ? accent + '0a' : '#fff',
        border: `1.5px solid ${isHov ? accent + '60' : '#f0f0f0'}`,
        boxShadow: isHov
          ? `0 8px 32px ${accent}20, 0 2px 8px ${accent}10`
          : '0 1px 6px rgba(0,0,0,0.04)',
        transform: inView ? (isHov ? 'translateX(6px)' : 'none') : 'translateY(28px)',
      }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {/* Left accent bar */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl transition-all duration-300"
        style={{ background: isHov ? `linear-gradient(to bottom, ${accent}, ${accentDark})` : accent + '30' }}
      />

      <div className="flex items-start gap-4 px-5 py-4 pl-6">
        {/* Index + dot */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0 mt-0.5">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-300"
            style={{ background: isHov ? `linear-gradient(135deg, ${accent}, ${accentDark})` : accent + '15' }}
          >
            <div className="w-2 h-2 rounded-full transition-all duration-300"
              style={{ background: isHov ? '#fff' : accent }} />
          </div>
          <span className="text-[9px] font-black tracking-widest" style={{ color: accent + '50' }}>
            {String(i + 1).padStart(2, '0')}
          </span>
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="font-black text-gray-900 text-[15px] leading-tight mb-1.5 transition-colors duration-200"
            style={{ color: isHov ? '#1a1a2e' : '#111827' }}>
            {sub.name}
          </p>
          <p className="text-gray-400 text-sm leading-relaxed">{sub.description}</p>
        </div>

        {/* Arrow */}
        <ArrowRight
          className="w-4 h-4 flex-shrink-0 mt-1 transition-all duration-300"
          style={{
            color: isHov ? accent : '#e5e7eb',
            transform: isHov ? 'translateX(3px)' : 'none',
            opacity: isHov ? 1 : 0,
          }}
        />
      </div>
    </div>
  );
};

/* ── Main page ── */
const InvestmentSectors = () => {
  usePageMeta(
    "Investment Sectors",
    "Explore Fundviser Capital's investment universe — real estate, bullion, equities, and startup & VC — a multi-sector portfolio engineered for long-term compounding."
  );
  const [activeTab, setActiveTab]             = useState(sectors[0].id);
  const [activeSubsector, setActiveSubsector] = useState(null);
  const detailRef  = useRef(null);
  const tabsRef    = useRef(null);
  const location   = useLocation();
  const [headerRef] = useInView(0.1, false);

  const activeIdx = sectors.findIndex(s => s.id === activeTab);

  /* Auto-scroll active tab into centre of the mobile strip */
  useEffect(() => {
    const container = tabsRef.current;
    if (!container) return;
    const activeBtn = container.querySelector('[data-active="true"]');
    if (activeBtn) activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeTab]);

  const active     = sectors.find((s) => s.id === activeTab);
  const ActiveIcon = active.icon;

  const currentImage = activeSubsector !== null && active.subsectors[activeSubsector]?.image
    ? active.subsectors[activeSubsector].image
    : active.mainImage;

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && sectors.find(s => s.id === hash)) {
      setActiveTab(hash);
      setActiveSubsector(null);
      setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [location.hash]);

  const handleSelectSector = (id) => {
    setActiveTab(id);
    setActiveSubsector(null);
    setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @keyframes imgReveal {
          from { opacity: 0; transform: scale(1.06); filter: blur(4px); }
          to   { opacity: 1; transform: scale(1);    filter: blur(0); }
        }
        @keyframes lineGrow {
          from { width: 0; opacity: 0; }
          to   { width: 64px; opacity: 1; }
        }
        @keyframes headerUp {
          from { opacity: 0; transform: translateY(22px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes glowPulse {
          0%,100% { opacity: 0.5; transform: scale(1); }
          50%      { opacity: 0.9; transform: scale(1.03); }
        }
        @keyframes blobA {
          0%,100% { transform: translate(0, 0)    scale(1); }
          33%      { transform: translate(30px,-20px) scale(1.08); }
          66%      { transform: translate(-20px,15px) scale(0.94); }
        }
        @keyframes blobB {
          0%,100% { transform: translate(0, 0)     scale(1); }
          33%      { transform: translate(-25px,18px)  scale(1.06); }
          66%      { transform: translate(20px,-12px)  scale(0.96); }
        }
        @keyframes pillGlow {
          0%,100% { box-shadow: 0 0 0 0 transparent; }
          50%      { box-shadow: 0 0 24px 4px var(--pill-accent); }
        }
        .img-reveal { animation: imgReveal 0.5s cubic-bezier(0.16,1,0.3,1) both; }
        .line-grow  { animation: lineGrow  0.6s cubic-bezier(0.16,1,0.3,1) 0.2s both; }
        .header-up  { animation: headerUp  0.55s cubic-bezier(0.16,1,0.3,1) both; }
      `}</style>

      <Navbar />

      {/* ════════════════ HERO ════════════════ */}
      <div className="relative pt-20 overflow-hidden" style={{
        background: 'linear-gradient(135deg, #0a0015 0%, #1e0038 30%, #360153 55%, #0f001f 100%)',
      }}>
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)',
          backgroundSize: '64px 64px',
        }} />

        {/* Animated blobs */}
        <div className="absolute top-10 left-1/4 w-72 h-72 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.25) 0%, transparent 70%)', animation: 'blobA 9s ease-in-out infinite' }} />
        <div className="absolute top-20 right-1/4 w-56 h-56 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.18) 0%, transparent 70%)', animation: 'blobB 12s ease-in-out infinite' }} />
        <div className="absolute bottom-16 left-1/3 w-48 h-48 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)', animation: 'blobA 15s ease-in-out infinite reverse' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-8 border backdrop-blur-md"
            style={{ background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.15)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
            <span className="text-yellow-400 text-xs font-black tracking-widest uppercase">Where We Invest</span>
          </div>

          {/* Title */}
          <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6 tracking-tight">
            Investment{' '}
            <span className="relative inline-block">
              <span className="text-transparent bg-clip-text"
                style={{ backgroundImage: 'linear-gradient(135deg, #fde68a 0%, #fbbf24 40%, #fde68a 100%)' }}>
                Sectors
              </span>
              {/* Underline glow */}
              <span className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full"
                style={{ background: 'linear-gradient(to right, transparent, #fbbf24, transparent)' }} />
            </span>
          </h1>

          <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed mb-14 font-light">
            Every investment is an opportunity to craft a lasting legacy. Our capital is strategically
            allocated across multiple asset classes for long-term growth and stability.
          </p>

          {/* Sector pills — desktop */}
          <div className="hidden md:flex flex-wrap justify-center gap-3 mt-2">
            {sectors.map((s) => {
              const Icon = s.icon;
              const isAct = s.id === activeTab;
              return (
                <button
                  key={s.id}
                  onClick={() => handleSelectSector(s.id)}
                  className="relative flex items-center gap-3 px-5 py-3.5 rounded-2xl border transition-all duration-300 hover:scale-105 active:scale-95 overflow-hidden group"
                  style={{
                    '--pill-accent': s.accent + '55',
                    background:  isAct ? 'rgba(255,255,255,0.13)' : 'rgba(255,255,255,0.05)',
                    borderColor: isAct ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(16px)',
                    boxShadow: isAct
                      ? `0 0 0 1px ${s.accent}40, 0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.15)`
                      : 'none',
                  }}
                >
                  {/* Active shimmer */}
                  {isAct && (
                    <span className="absolute inset-0 rounded-2xl pointer-events-none"
                      style={{ background: `linear-gradient(135deg, ${s.accent}15, transparent 60%)` }} />
                  )}
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300"
                    style={{ background: isAct ? s.accent + '50' : s.accent + '25' }}
                  >
                    <Icon className="w-4.5 h-4.5 text-white" style={{ width: 18, height: 18 }} />
                  </div>
                  <div className="text-left">
                    <p className="text-[9px] font-black tracking-[0.2em] uppercase text-white/30 leading-none mb-0.5">{s.number}</p>
                    <p className="text-sm font-bold text-white leading-none">{s.title}</p>
                  </div>
                  {isAct && (
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 ml-1"
                      style={{ background: s.accent, boxShadow: `0 0 8px ${s.accent}` }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Wave */}
        <div className="relative h-20">
          <svg viewBox="0 0 1440 80" className="absolute bottom-0 w-full" preserveAspectRatio="none">
            <path d="M0,80 L1440,80 L1440,0 Q1080,80 720,50 Q360,20 0,60 Z" fill="white" />
          </svg>
        </div>
      </div>

      {/* ════════════════ DESKTOP DETAIL ════════════════ */}
      <div ref={detailRef} className="hidden md:block">

        {/* Sector progress bar */}
        <div className="sticky top-[72px] z-30 border-b border-gray-100/80"
          style={{ background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(20px)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-1 py-1">
              {sectors.map((s, i) => {
                const Icon = s.icon;
                const isAct = s.id === activeTab;
                return (
                  <button
                    key={s.id}
                    onClick={() => handleSelectSector(s.id)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all duration-250 relative group"
                    style={{
                      color: isAct ? active.accent : '#9ca3af',
                      background: isAct ? active.accent + '10' : 'transparent',
                    }}
                  >
                    <Icon style={{ width: 14, height: 14 }} />
                    <span className="hidden lg:block">{s.title}</span>
                    <span className="lg:hidden">{s.number}</span>
                    {isAct && (
                      <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
                        style={{ background: active.accent }} />
                    )}
                  </button>
                );
              })}
              <div className="ml-auto flex items-center gap-1.5 text-gray-400 text-xs font-bold pr-2">
                <span className="font-black" style={{ color: active.accent }}>{active.number}</span>
                <span>/</span>
                <span>04</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="relative overflow-hidden">
          {/* Subtle background radial */}
          <div className="absolute inset-0 pointer-events-none transition-all duration-700"
            style={{ background: `radial-gradient(ellipse 60% 50% at 50% 0%, ${active.accent}06 0%, transparent 70%)` }} />

          <div key={activeTab} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 relative">

            {/* Ghost number */}
            <div className="absolute inset-0 flex items-start justify-center pointer-events-none select-none overflow-hidden"
              style={{ paddingTop: '2rem' }}>
              <span className="font-black transition-all duration-500"
                style={{
                  fontSize: 'clamp(180px, 22vw, 320px)',
                  lineHeight: 1,
                  letterSpacing: '-0.06em',
                  color: active.accent + '06',
                  WebkitTextStroke: `2px ${active.accent}08`,
                }}>
                {active.number}
              </span>
            </div>

            {/* ── Centered header ── */}
            <div ref={headerRef} className="relative text-center max-w-3xl mx-auto mb-16">

              {/* Icon with glow ring */}
              <div className="relative inline-flex mb-8">
                <div className="absolute inset-0 rounded-3xl blur-xl transition-all duration-700"
                  style={{ background: `${active.accent}35`, animation: 'glowPulse 3s ease-in-out infinite' }} />
                <div className={`relative inline-flex p-5 rounded-3xl bg-gradient-to-br ${active.color} shadow-2xl`}
                  style={{ boxShadow: `0 20px 60px ${active.accent}35` }}>
                  <ActiveIcon className="w-10 h-10 text-white" />
                </div>
              </div>

              {/* Number label */}
              <div className="header-up flex items-center justify-center gap-3 mb-4">
                <div className="h-px w-8 rounded-full" style={{ background: active.accent + '40' }} />
                <span className="text-xs font-black tracking-[0.25em] uppercase" style={{ color: active.accent }}>
                  Sector {active.number}
                </span>
                <div className="h-px w-8 rounded-full" style={{ background: active.accent + '40' }} />
              </div>

              {/* Title */}
              <h2 className="header-up text-5xl md:text-6xl font-black mb-4 leading-tight tracking-tight"
                style={{
                  background: `linear-gradient(135deg, #111827 0%, ${active.accent} 60%, ${active.accentDark} 100%)`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                  animationDelay: '60ms',
                }}>
                {active.title}
              </h2>

              {/* Tagline */}
              <p className="header-up text-lg font-semibold mb-6" style={{ color: active.accent, animationDelay: '100ms' }}>
                {active.tagline}
              </p>

              {/* Animated divider */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-px rounded-full" style={{ width: 40, background: `linear-gradient(to right, transparent, ${active.accent}40)` }} />
                <div className="line-grow h-0.5 rounded-full" style={{ background: `linear-gradient(to right, ${active.accent}, ${active.accentDark})` }} />
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: active.accent }} />
                <div className="line-grow h-0.5 rounded-full" style={{ background: `linear-gradient(to left, ${active.accent}, ${active.accentDark})` }} />
                <div className="h-px rounded-full" style={{ width: 40, background: `linear-gradient(to left, transparent, ${active.accent}40)` }} />
              </div>

              {/* Summary */}
              <p className="header-up text-gray-500 text-base leading-relaxed max-w-2xl mx-auto" style={{ animationDelay: '140ms' }}>
                {active.summary}
              </p>
            </div>

            {/* ── Two columns ── */}
            <div className="grid lg:grid-cols-5 gap-8 xl:gap-12 items-center">

              {/* LEFT — Subsectors */}
              <div className="lg:col-span-2 space-y-3">
                <div className="flex items-center gap-2 mb-5">
                  <div className="h-px flex-1" style={{ background: `linear-gradient(to right, ${active.accent}30, transparent)` }} />
                  <span className="text-[10px] font-black tracking-[0.22em] uppercase" style={{ color: active.accent }}>
                    Investment Areas
                  </span>
                </div>
                {active.subsectors.map((sub, i) => (
                  <SubCard
                    key={`${activeTab}-${i}`}
                    sub={sub}
                    i={i}
                    isHov={activeSubsector === i}
                    accent={active.accent}
                    accentDark={active.accentDark}
                    onEnter={() => setActiveSubsector(i)}
                    onLeave={() => setActiveSubsector(null)}
                  />
                ))}

                {/* Stat chip below subsectors */}
                <div className="pt-3">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl"
                    style={{ background: active.accent + '10', border: `1px solid ${active.accent}25` }}>
                    <div className="w-2 h-2 rounded-full" style={{ background: active.accent }} />
                    <span className="text-sm font-black" style={{ color: active.accent }}>
                      {active.stat.value} {active.stat.label}
                    </span>
                    <span className="text-xs text-gray-400">in this sector</span>
                  </div>
                </div>
              </div>

              {/* RIGHT — Image */}
              <div className="lg:col-span-3 relative">
                {/* Outer glow */}
                <div className="absolute -inset-4 rounded-[2.5rem] pointer-events-none transition-all duration-700"
                  style={{
                    background: `radial-gradient(ellipse at center, ${active.accent}18 0%, transparent 70%)`,
                    animation: 'glowPulse 4s ease-in-out infinite',
                  }} />

                {/* Image wrapper */}
                <div className="relative rounded-3xl overflow-hidden"
                  style={{
                    boxShadow: `0 32px 80px ${active.accent}20, 0 8px 24px rgba(0,0,0,0.12)`,
                    border: `1px solid ${active.accent}20`,
                  }}>

                  {/* Image */}
                  <img
                    key={currentImage}
                    src={currentImage}
                    alt={activeSubsector !== null ? active.subsectors[activeSubsector].name : active.title}
                    className="img-reveal w-full object-cover"
                    style={{ height: 460 }}
                  />

                  {/* Color tint overlay */}
                  <div className="absolute inset-0 pointer-events-none transition-all duration-500"
                    style={{ background: `linear-gradient(135deg, ${active.accent}15 0%, transparent 50%, rgba(0,0,0,0.05) 100%)` }} />

                  {/* Top-right stat chip */}
                  <div className="absolute top-4 right-4">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl backdrop-blur-md"
                      style={{ background: 'rgba(255,255,255,0.92)', border: `1px solid ${active.accent}30` }}>
                      <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: active.accent }} />
                      <span className="text-xs font-black" style={{ color: active.accent }}>
                        {active.stat.value} {active.stat.label}
                      </span>
                    </div>
                  </div>

                  {/* Bottom overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 backdrop-blur-[2px]"
                    style={{ background: `linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)` }}>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-white font-black text-lg leading-tight mb-1">
                          {activeSubsector !== null ? active.subsectors[activeSubsector].name : active.title}
                        </p>
                        <p className="text-white/65 text-sm leading-relaxed max-w-xs">
                          {activeSubsector !== null ? active.subsectors[activeSubsector].description : active.tagline}
                        </p>
                      </div>
                      <div className="flex-shrink-0 ml-4">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ background: `linear-gradient(135deg, ${active.accent}, ${active.accentDark})` }}>
                          <ActiveIcon className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Subsector dots below image */}
                <div className="flex items-center justify-center gap-2 mt-5">
                  {active.subsectors.map((_, dotIdx) => (
                    <button
                      key={dotIdx}
                      onClick={() => setActiveSubsector(activeSubsector === dotIdx ? null : dotIdx)}
                      className="rounded-full transition-all duration-300"
                      style={{
                        width: activeSubsector === dotIdx ? 24 : 8,
                        height: 8,
                        background: activeSubsector === dotIdx ? active.accent : active.accent + '30',
                        boxShadow: activeSubsector === dotIdx ? `0 0 10px ${active.accent}70` : 'none',
                      }}
                    />
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ════════════════ MOBILE DETAIL ════════════════ */}
      <div className="md:hidden" style={{ background: '#f7f8fc' }}>

        {/* Mobile header */}
        <div className="px-5 pt-10 pb-3 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="h-px w-6" style={{ background: active.accent + '40' }} />
            <span className="text-[10px] font-black tracking-[0.22em] uppercase" style={{ color: active.accent }}>
              Our Portfolio
            </span>
            <div className="h-px w-6" style={{ background: active.accent + '40' }} />
          </div>
          <h2 className="text-3xl font-black text-gray-900 mb-1 leading-tight">
            Investment{' '}
            <span key={activeTab} style={{ color: active.accent, transition: 'color 0.4s ease' }}>
              Sectors
            </span>
          </h2>
          <p className="text-gray-400 text-sm">Tap a sector to explore</p>
        </div>

        {/* Horizontal scrolling tabs with prev/next arrows */}
        <div className="px-4 py-4 flex items-center gap-2">

          {/* Left arrow */}
          <button
            onClick={() => activeIdx > 0 && handleSelectSector(sectors[activeIdx - 1].id)}
            disabled={activeIdx === 0}
            className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200"
            style={{
              background: activeIdx === 0 ? '#f1f2f6' : active.accent + '18',
              border: `1.5px solid ${activeIdx === 0 ? '#e8eaf0' : active.accent + '40'}`,
              color: activeIdx === 0 ? '#d1d5db' : active.accent,
            }}
          >
            <ChevronLeft style={{ width: 16, height: 16 }} />
          </button>

          {/* Scrollable strip */}
          <div className="relative flex-1 overflow-hidden">
            {/* Right fade hint */}
            {activeIdx < sectors.length - 1 && (
              <div className="absolute right-0 top-0 bottom-0 w-8 pointer-events-none z-10"
                style={{ background: 'linear-gradient(to left, #f7f8fc, transparent)' }} />
            )}
            {/* Left fade hint */}
            {activeIdx > 0 && (
              <div className="absolute left-0 top-0 bottom-0 w-8 pointer-events-none z-10"
                style={{ background: 'linear-gradient(to right, #f7f8fc, transparent)' }} />
            )}

            <div
              ref={tabsRef}
              className="flex gap-2.5 overflow-x-auto"
              style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
            >
              {sectors.map(s => {
                const Icon = s.icon;
                const isAct = s.id === activeTab;
                return (
                  <button
                    key={s.id}
                    data-active={isAct ? 'true' : 'false'}
                    onClick={() => handleSelectSector(s.id)}
                    className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-2xl transition-all duration-300"
                    style={{
                      background: isAct ? `linear-gradient(135deg, ${s.accent}, ${s.accentDark})` : '#fff',
                      border:     `1.5px solid ${isAct ? 'transparent' : '#e8eaf0'}`,
                      boxShadow:  isAct ? `0 6px 20px ${s.accent}40` : '0 1px 4px rgba(0,0,0,0.05)',
                      color:      isAct ? '#fff' : '#6b7280',
                      transform:  isAct ? 'translateY(-2px)' : 'none',
                    }}
                  >
                    <Icon style={{ width: 14, height: 14 }} />
                    <span className="text-sm font-bold whitespace-nowrap">{s.title}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right arrow */}
          <button
            onClick={() => activeIdx < sectors.length - 1 && handleSelectSector(sectors[activeIdx + 1].id)}
            disabled={activeIdx === sectors.length - 1}
            className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200"
            style={{
              background: activeIdx === sectors.length - 1 ? '#f1f2f6' : active.accent + '18',
              border: `1.5px solid ${activeIdx === sectors.length - 1 ? '#e8eaf0' : active.accent + '40'}`,
              color: activeIdx === sectors.length - 1 ? '#d1d5db' : active.accent,
            }}
          >
            <ChevronRight style={{ width: 16, height: 16 }} />
          </button>

        </div>

        {/* Sector detail */}
        <div className="px-4 pb-14 space-y-4">

          {/* Image card */}
          <div className="relative rounded-3xl overflow-hidden" style={{ aspectRatio: '16/10' }}>
            <img
              key={`mob-${activeTab}-${activeSubsector}`}
              src={activeSubsector !== null && active.subsectors[activeSubsector]?.image
                ? active.subsectors[activeSubsector].image : active.mainImage}
              alt={active.title}
              className="img-reveal w-full h-full"
              style={{ objectFit: 'cover' }}
            />
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(to top, rgba(5,0,15,0.9) 0%, rgba(5,0,15,0.3) 50%, transparent 100%)',
            }} />
            <div className="absolute inset-0 transition-all duration-500" style={{
              background: `linear-gradient(135deg, ${active.accent}25 0%, transparent 55%)`,
            }} />
            {/* Ghost number */}
            <div className="absolute right-3 top-2 font-black select-none pointer-events-none"
              style={{ fontSize: 80, lineHeight: 1, color: 'rgba(255,255,255,0.07)', letterSpacing: '-0.05em' }}>
              {active.number}
            </div>
            {/* Content overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-1.5 rounded-lg bg-gradient-to-br ${active.color}`}>
                  <ActiveIcon className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-[9px] font-black tracking-widest uppercase" style={{ color: active.accent }}>
                  {active.number} of 04
                </span>
              </div>
              <h3 className="text-white font-black text-xl leading-tight mb-0.5">{active.title}</h3>
              <p className="text-xs font-semibold" style={{ color: active.accent }}>{active.tagline}</p>
            </div>
            {activeSubsector !== null && (
              <div className="absolute top-3 left-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black tracking-wider"
                  style={{ background: 'rgba(255,255,255,0.92)', color: active.accent, border: `1px solid ${active.accent}30` }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: active.accent }} />
                  {active.subsectors[activeSubsector].name}
                </span>
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="rounded-2xl p-5"
            style={{ background: '#fff', borderLeft: `4px solid ${active.accent}`, boxShadow: '0 2px 16px rgba(0,0,0,0.05)' }}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[9px] font-black tracking-[0.22em] uppercase" style={{ color: active.accent }}>Overview</span>
              <div className="h-px flex-1" style={{ background: `linear-gradient(to right, ${active.accent}25, transparent)` }} />
            </div>
            <p className="text-gray-500 text-sm leading-relaxed">{active.summary}</p>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-xl"
              style={{ background: active.accent + '0f', border: `1px solid ${active.accent}25` }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: active.accent }} />
              <span className="text-xs font-black" style={{ color: active.accent }}>{active.stat.value} {active.stat.label}</span>
            </div>
          </div>

          {/* Subsectors */}
          <div className="space-y-2">
            <p className="text-[9px] font-black tracking-[0.22em] uppercase px-1 mb-3" style={{ color: active.accent }}>
              Investment Areas
            </p>
            {active.subsectors.map((sub, i) => {
              const isActiveSub = activeSubsector === i;
              return (
                <div
                  key={`${activeTab}-mob-${i}`}
                  className="rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer"
                  style={{
                    background: '#fff',
                    border: `1.5px solid ${isActiveSub ? active.accent : '#eef0f4'}`,
                    boxShadow: isActiveSub ? `0 8px 28px ${active.accent}20` : '0 1px 6px rgba(0,0,0,0.04)',
                  }}
                  onClick={() => setActiveSubsector(isActiveSub ? null : i)}
                >
                  <div className="flex items-center gap-3 px-4 py-3.5">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300"
                      style={{ background: isActiveSub ? `linear-gradient(135deg, ${active.accent}, ${active.accentDark})` : active.accent + '15' }}>
                      <div className="w-2 h-2 rounded-full transition-all duration-300"
                        style={{ background: isActiveSub ? '#fff' : active.accent }} />
                    </div>
                    <p className="text-gray-900 font-black text-sm flex-1">{sub.name}</p>
                    <ChevronRight className="w-4 h-4 flex-shrink-0 transition-all duration-300"
                      style={{ color: active.accent, transform: isActiveSub ? 'rotate(90deg)' : 'none' }} />
                  </div>
                  {isActiveSub && (
                    <div className="px-4 pb-4">
                      {sub.image && (
                        <div className="rounded-xl overflow-hidden mb-3" style={{ height: 140 }}>
                          <img src={sub.image} alt={sub.name} className="w-full h-full img-reveal"
                            style={{ objectFit: 'cover' }} />
                        </div>
                      )}
                      <p className="text-gray-400 text-sm leading-relaxed">{sub.description}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </div>

      {/* ════════════════ CTA ════════════════ */}
      <div className="relative overflow-hidden py-24 px-4"
        style={{ background: 'linear-gradient(135deg, #0a0015 0%, #1e0038 40%, #360153 70%, #0f001f 100%)' }}>

        {/* Grid */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        {/* Blobs */}
        <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.18) 0%, transparent 70%)', animation: 'blobA 10s ease-in-out infinite' }} />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.12) 0%, transparent 70%)', animation: 'blobB 13s ease-in-out infinite' }} />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-8 border"
            style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
            <span className="text-yellow-400 text-xs font-black tracking-widest uppercase">Fundviser Capital</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-black text-white mb-5 leading-tight">
            Interested in Investing<br className="hidden md:block" /> With Us?
          </h2>
          <p className="text-white/50 text-lg mb-12 leading-relaxed max-w-2xl mx-auto font-light">
            Our team is ready to guide you through the investment opportunities best suited to your financial goals and risk profile.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
            <a href="mailto:info@fundvisercapital.in"
              className="flex items-center justify-center gap-2.5 px-8 py-4 rounded-2xl font-bold shadow-xl transition-all duration-300 hover:scale-105 hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', color: '#1a1a1a' }}>
              <Mail className="w-5 h-5" />
              info@fundvisercapital.in
            </a>
            <a href="tel:+912231236586"
              className="flex items-center justify-center gap-2.5 px-8 py-4 rounded-2xl font-bold border transition-all duration-300 hover:scale-105 backdrop-blur-md"
              style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.18)', color: '#fff' }}>
              <Phone className="w-5 h-5" />
              +91-22-31236586
            </a>
          </div>

          <div className="flex items-center justify-center gap-2 text-white/30 text-sm">
            <MapPin className="w-4 h-4" />
            22/7 Manek Mahal, 90 Veer Nariman Road, Churchgate, Mumbai 400020
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InvestmentSectors;
